import { useNavigate } from 'react-router-dom';
import { Search, Bell, ChevronDown, LogOut, Settings, HelpCircle } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Avatar } from '@/components/ui';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/utils/cn';

export function TopNav() {
  const navigate = useNavigate();
  const { member, platformAdmin, logout } = useAuthStore();
  const displayName = member?.display_name || platformAdmin?.display_name || platformAdmin?.email || 'Member';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-4 border-b border-border bg-surface px-4 print:hidden md:px-6">
      <div className="relative hidden flex-1 max-w-sm md:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" aria-hidden="true" />
        <input
          type="search"
          placeholder="Search…"
          aria-label="Global search"
          className="h-9 w-full rounded border border-border bg-surface-secondary pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        />
      </div>

      <div className="ml-auto flex items-center gap-3">
        <button
          type="button"
          aria-label="Notifications"
          className="relative flex h-[34px] w-[34px] items-center justify-center rounded-full text-text-secondary hover:bg-surface-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-danger" aria-hidden="true" />
        </button>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            className={cn(
              'flex items-center gap-2 rounded border border-border bg-surface-secondary p-1.5 text-sm font-medium text-text-primary hover:bg-surface',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
            )}
          >
            <Avatar name={displayName} size="sm" />
            <span className="hidden md:inline">{displayName}</span>
            <ChevronDown className="h-4 w-4 text-text-secondary" aria-hidden="true" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 min-w-[180px] rounded border border-border bg-surface p-1 shadow-dropdown"
            >
              <DropdownMenu.Item
                onSelect={() => navigate('/settings')}
                className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-text-primary outline-none data-[highlighted]:bg-surface-secondary"
              >
                <Settings className="h-4 w-4" aria-hidden="true" /> Settings
              </DropdownMenu.Item>
              <DropdownMenu.Item className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-text-primary outline-none data-[highlighted]:bg-surface-secondary">
                <HelpCircle className="h-4 w-4" aria-hidden="true" /> Help
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={handleLogout}
                className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-danger outline-none data-[highlighted]:bg-danger-light"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" /> Sign Out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
